/**
 * /ev-charger-commands - tenant EV DLM command audit log
 * (DEVELOPER_CONTINUATION_GUIDE §18.1).
 *
 * Viewer-readable list over /v1/ev-charger-commands. Operators see two
 * extra actions:
 *   - "New command" — opens an inline form that POSTs to the same surface;
 *     the API records `state="pending"` only. No charger write happens here.
 *   - "Cancel" on each pending row — POSTs a state transition to
 *     `cancelled` so the poller-side executor (PR #40) ignores it.
 *
 * The page intentionally does not render charger telemetry yet:
 * `EMS_POLLER_ENABLED=false` on TEST means there's no live EV feed to
 * surface, and a separate page is the right home once that flips on.
 */
import {
  ActionIcon,
  Badge,
  Breadcrumbs,
  Button,
  Center,
  Group,
  Loader,
  Modal,
  NumberInput,
  Pagination,
  Paper,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
  Tooltip,
} from "@mantine/core";
import {
  IconBolt,
  IconCircleX,
  IconPlus,
  IconRefresh,
  IconSearch,
} from "@tabler/icons-react";
import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";

import { ApiError } from "@/api/client";
import {
  useCreateEvChargerCommand,
  useEvChargerCommands,
  useTransitionEvChargerCommand,
  type EvChargerCommandsParams,
} from "@/api/queries";
import {
  EV_CHARGER_COMMAND_SPECS,
  EV_CHARGER_COMMAND_STATES,
  EV_CHARGER_COMMAND_TYPES,
  type EvChargerCommand,
  type EvChargerCommandState,
  type EvChargerCommandType,
} from "@/api/types";
import { useAuth } from "@/auth/AuthProvider";
import { ErrorBanner } from "@/components/ErrorBanner";
import { SiteSelect } from "@/components/SiteSelect";

const PAGE_SIZE = 50;

type FiltersState = {
  site_slug: string;
  device_slug: string;
  state: EvChargerCommandState | "";
};

const EMPTY_FILTERS: FiltersState = {
  site_slug: "",
  device_slug: "",
  state: "pending",
};

const STATE_OPTIONS = EV_CHARGER_COMMAND_STATES.map((state) => ({
  value: state,
  label: capitalize(state),
}));

const COMMAND_TYPE_OPTIONS = EV_CHARGER_COMMAND_TYPES.map((type) => {
  const spec = EV_CHARGER_COMMAND_SPECS[type];
  return {
    value: type,
    label: `${type} — ${spec.description} (${spec.min}–${spec.max} ${spec.unit})`,
  };
});

const DATE_TIME = new Intl.DateTimeFormat(undefined, {
  dateStyle: "medium",
  timeStyle: "short",
});

export function EvChargerCommandsPage() {
  const auth = useAuth();
  const [filters, setFilters] = useState<FiltersState>(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const [actionError, setActionError] = useState<unknown | null>(null);
  const [pendingCancelId, setPendingCancelId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const canMutate =
    auth.status === "authenticated" &&
    auth.profile.roles.some((role) => role === "operator" || role === "admin");

  const queryParams = useMemo<EvChargerCommandsParams>(
    () => ({
      site_slug: filters.site_slug.trim() || undefined,
      device_slug: filters.device_slug.trim() || undefined,
      state: filters.state || undefined,
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
    }),
    [filters, page],
  );

  const commandsQuery = useEvChargerCommands(queryParams);
  const transitionMutation = useTransitionEvChargerCommand();

  const items = commandsQuery.data?.items ?? [];
  const total = commandsQuery.data?.page.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const setFilter = (k: keyof FiltersState, v: string) => {
    setFilters((f) => ({ ...f, [k]: v }));
    setPage(1);
  };

  const cancel = async (command: EvChargerCommand) => {
    setActionError(null);
    setPendingCancelId(command.id);
    try {
      await transitionMutation.mutateAsync({
        id: command.id,
        body: { state: "cancelled" },
      });
    } catch (err) {
      setActionError(err);
    } finally {
      setPendingCancelId(null);
    }
  };

  return (
    <Stack>
      <Breadcrumbs>
        <Text>Operations</Text>
        <Text>EV charger commands</Text>
      </Breadcrumbs>

      <Group justify="space-between" align="flex-start">
        <Stack gap={4}>
          <Title order={1}>EV charger commands</Title>
          <Text c="dimmed" size="sm">
            Tenant-scoped audit log for EV DC charger DLM commands. Operators
            create commands here; the poller-side executor consumes the
            pending rows. No charger write happens until the executor is
            wired to a real Modbus writer.
          </Text>
        </Stack>
        <Group gap="xs">
          <Tooltip label="Refresh list">
            <ActionIcon
              variant="default"
              size="lg"
              onClick={() => commandsQuery.refetch()}
              loading={commandsQuery.isFetching}
              aria-label="Refresh"
            >
              <IconRefresh size={18} />
            </ActionIcon>
          </Tooltip>
          <Tooltip
            label={canMutate ? "Create command" : "Operator or admin role required"}
          >
            <Button
              leftSection={<IconPlus size={14} />}
              disabled={!canMutate}
              onClick={() => setCreateOpen(true)}
            >
              New command
            </Button>
          </Tooltip>
        </Group>
      </Group>

      <Paper withBorder p="md" radius="md">
        <Group gap="sm" wrap="wrap">
          <SiteSelect
            label="Site"
            placeholder="any"
            clearable
            value={filters.site_slug || null}
            onChange={(v) => setFilter("site_slug", v ?? "")}
            w={240}
          />
          <TextInput
            label="Device slug"
            placeholder="e.g. ev_charger_1"
            leftSection={<IconSearch size={14} />}
            value={filters.device_slug}
            onChange={(e) => setFilter("device_slug", e.currentTarget.value)}
            w={220}
          />
          <Select
            label="State"
            placeholder="any"
            data={STATE_OPTIONS}
            clearable
            value={filters.state || null}
            onChange={(v) => setFilter("state", v ?? "")}
            w={180}
          />
        </Group>
      </Paper>

      {commandsQuery.error && <ErrorBanner error={commandsQuery.error} />}
      {actionError !== null && (
        <ErrorBanner error={actionError} title="Cancel failed" />
      )}

      <Paper withBorder p="lg" radius="md">
        <Group justify="space-between" mb="md">
          <Group gap="xs">
            <IconBolt size={20} />
            <Title order={3}>Results</Title>
          </Group>
          <Text c="dimmed" size="sm">
            {total} command{total === 1 ? "" : "s"}
          </Text>
        </Group>

        {commandsQuery.isLoading ? (
          <Center py="xl">
            <Loader />
          </Center>
        ) : items.length === 0 ? (
          <Text c="dimmed">No commands match the current filters.</Text>
        ) : (
          <Table highlightOnHover verticalSpacing="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Command</Table.Th>
                <Table.Th>Site / Device</Table.Th>
                <Table.Th>Value</Table.Th>
                <Table.Th>State</Table.Th>
                <Table.Th>Requested</Table.Th>
                <Table.Th>Last error</Table.Th>
                <Table.Th style={{ textAlign: "right" }}>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {items.map((command) => (
                <Table.Tr key={command.id}>
                  <Table.Td>
                    <Stack gap={0}>
                      <Text fw={500}>{command.command_type}</Text>
                      <Text c="dimmed" size="xs">
                        register {command.register_address} ·{" "}
                        {command.safety_min_value}–{command.safety_max_value}{" "}
                        {command.unit}
                      </Text>
                    </Stack>
                  </Table.Td>
                  <Table.Td>
                    <Stack gap={0}>
                      <Text size="sm">
                        <Link to={`/sites/${command.site_slug}`}>
                          {command.site_slug}
                        </Link>
                      </Text>
                      <Text c="dimmed" size="xs">
                        <Link
                          to={`/sites/${command.site_slug}/devices/${command.device_slug}`}
                        >
                          {command.device_slug}
                        </Link>
                      </Text>
                    </Stack>
                  </Table.Td>
                  <Table.Td>
                    <Text fw={500}>
                      {command.value} {command.unit}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge variant="light" color={stateColor(command.state)}>
                      {command.state}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Stack gap={0}>
                      <Text size="sm">{formatDate(command.requested_at)}</Text>
                      <Text c="dimmed" size="xs">
                        {command.requested_by}
                      </Text>
                    </Stack>
                  </Table.Td>
                  <Table.Td>
                    {command.last_error ? (
                      <Text c="red" size="xs">
                        {command.last_error}
                      </Text>
                    ) : (
                      <Text c="dimmed" size="sm">
                        -
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td style={{ textAlign: "right" }}>
                    {command.state === "pending" ? (
                      <Tooltip
                        label={
                          canMutate
                            ? "Cancel this pending command"
                            : "Operator or admin role required"
                        }
                      >
                        <Button
                          size="xs"
                          variant="light"
                          color="red"
                          leftSection={<IconCircleX size={14} />}
                          disabled={!canMutate}
                          loading={pendingCancelId === command.id}
                          onClick={() => void cancel(command)}
                          aria-label={`Cancel ${command.command_type} for ${command.device_slug}`}
                        >
                          Cancel
                        </Button>
                      </Tooltip>
                    ) : (
                      <Text c="dimmed" size="sm">
                        -
                      </Text>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}

        {totalPages > 1 && (
          <Group justify="center" mt="md">
            <Pagination value={page} onChange={setPage} total={totalPages} />
          </Group>
        )}
      </Paper>

      <CreateCommandModal
        opened={createOpen}
        onClose={() => setCreateOpen(false)}
        onSuccess={() => setCreateOpen(false)}
      />
    </Stack>
  );
}

type CreateValues = {
  site_slug: string;
  device_slug: string;
  command_type: EvChargerCommandType | "";
  value: number | "";
  reason: string;
};

const EMPTY_CREATE: CreateValues = {
  site_slug: "",
  device_slug: "",
  command_type: "",
  value: "",
  reason: "",
};

type CreateModalProps = {
  opened: boolean;
  onClose: () => void;
  onSuccess: () => void;
};

function CreateCommandModal({ opened, onClose, onSuccess }: CreateModalProps) {
  const createMutation = useCreateEvChargerCommand();
  const [values, setValues] = useState<CreateValues>(EMPTY_CREATE);
  const [errors, setErrors] = useState<Partial<Record<keyof CreateValues, string>>>({});

  useEffect(() => {
    if (opened) {
      setValues(EMPTY_CREATE);
      setErrors({});
      createMutation.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened]);

  const set = <K extends keyof CreateValues>(field: K, value: CreateValues[K]) => {
    setValues((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => {
      if (!(field in prev)) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const spec =
    values.command_type !== "" ? EV_CHARGER_COMMAND_SPECS[values.command_type] : null;

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const next: Partial<Record<keyof CreateValues, string>> = {};
    if (values.site_slug.trim().length === 0) next.site_slug = "required";
    if (values.device_slug.trim().length === 0) next.device_slug = "required";
    if (values.command_type === "") next.command_type = "required";
    if (typeof values.value !== "number" || !Number.isInteger(values.value)) {
      next.value = "required (integer)";
    } else if (spec && (values.value < spec.min || values.value > spec.max)) {
      next.value = `must be ${spec.min}..${spec.max} ${spec.unit}`;
    }
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    try {
      await createMutation.mutateAsync({
        site_slug: values.site_slug.trim(),
        device_slug: values.device_slug.trim(),
        command_type: values.command_type as EvChargerCommandType,
        value: values.value as number,
        reason: values.reason.trim() ? values.reason.trim() : undefined,
      });
      onSuccess();
    } catch {
      // surfaced via createMutation.error below
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Create EV charger command"
      centered
      size="md"
    >
      <form onSubmit={handleSubmit} noValidate>
        <Stack>
          {createMutation.error && (
            <ErrorBanner
              error={createMutation.error}
              title={createCommandErrorTitle(createMutation.error)}
            />
          )}
          <SiteSelect
            label="Site"
            description="Site that owns the EV charger device."
            required
            value={values.site_slug || null}
            onChange={(v) => set("site_slug", v ?? "")}
            error={errors.site_slug}
          />
          <TextInput
            label="Device slug"
            description="EV charger device slug (kind=ev_charger)."
            required
            value={values.device_slug}
            onChange={(e) => set("device_slug", e.currentTarget.value)}
            error={errors.device_slug}
          />
          <Select
            label="Command type"
            description="Each type maps to a specific holding register on the charger."
            required
            data={COMMAND_TYPE_OPTIONS}
            value={values.command_type || null}
            onChange={(v) =>
              set("command_type", (v ?? "") as EvChargerCommandType | "")
            }
            error={errors.command_type}
          />
          <NumberInput
            label="Value"
            description={
              spec
                ? `Integer ${spec.unit} value, ${spec.min}..${spec.max}.`
                : "Pick a command type to see allowed range."
            }
            required
            min={spec?.min}
            max={spec?.max}
            value={values.value}
            onChange={(v) => set("value", typeof v === "number" ? v : "")}
            error={errors.value}
          />
          <TextInput
            label="Reason (optional)"
            description="Free-text note that lands in the audit row."
            value={values.reason}
            onChange={(e) => set("reason", e.currentTarget.value)}
          />
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" loading={createMutation.isPending}>
              Create
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}

function createCommandErrorTitle(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.code === "site_not_found") return "Site not found";
    if (err.code === "device_not_found") return "Device not found";
    if (err.code === "invalid_ev_charger_target") return "Not an EV charger";
    if (err.code === "invalid_ev_charger_command") return "Validation failed";
  }
  return "Create failed";
}

function capitalize(value: string): string {
  return value.slice(0, 1).toUpperCase() + value.slice(1);
}

function formatDate(value: string | null): string {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return DATE_TIME.format(date);
}

function stateColor(state: EvChargerCommandState): string {
  switch (state) {
    case "pending":
      return "blue";
    case "dispatched":
      return "indigo";
    case "succeeded":
      return "green";
    case "failed":
      return "red";
    case "cancelled":
      return "gray";
    default:
      return "gray";
  }
}
